import { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminOnboardingReview from './pages/AdminOnboardingReview';
import CoachDashboard from './pages/CoachDashboard';
import StartupOnboarding from './pages/StartupOnboarding';
import AdminBenchmarks from './pages/AdminBenchmarks';
import IdeaValidation from './pages/IdeaValidation';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor — attach token from localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export { API };

// Extract session_id from URL (hash or query params)
function extractSessionId() {
  // Check hash: /#session_id=xxx or /dashboard#session_id=xxx
  const hash = window.location.hash;
  if (hash) {
    const hashParams = new URLSearchParams(hash.substring(1));
    const sid = hashParams.get('session_id');
    if (sid) return sid;
  }
  // Check query params: /?session_id=xxx
  const queryParams = new URLSearchParams(window.location.search);
  const qid = queryParams.get('session_id');
  if (qid) return qid;
  return null;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hasStartup, setHasStartup] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [pendingSessionId, setPendingSessionId] = useState(null);

  useEffect(() => {
    const sessionId = extractSessionId();
    if (sessionId) {
      // Clean URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);
      setPendingSessionId(sessionId);
      exchangeSession(sessionId);
    } else {
      checkExistingSession();
    }
  }, []);

  const exchangeSession = async (sessionId) => {
    setAuthError(null);
    try {
      const response = await axios.post(`${API}/auth/session`, null, {
        params: { session_id: sessionId },
        withCredentials: true
      });

      const sessionToken = response.data.session_token;
      if (sessionToken) {
        localStorage.setItem('session_token', sessionToken);
      }

      const userData = response.data.user;

      // Determine destination
      let dest = '/onboarding';
      if (userData.role === 'admin') {
        dest = '/admin';
      } else if (userData.role === 'coach') {
        dest = '/coach';
      } else {
        try {
          const res = await axios.get(`${API}/startups`, {
            withCredentials: true,
            headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}
          });
          if (Array.isArray(res.data) && res.data.length > 0) {
            dest = '/dashboard';
          }
        } catch {}
      }

      // Full page redirect — guarantees clean state
      window.location.replace(dest);
    } catch (error) {
      console.error('Session exchange failed:', error?.response?.data || error.message);
      localStorage.removeItem('session_token');
      setAuthError(error?.response?.data?.detail || error.message || 'Authentication failed');
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setUser(userData);
      if (userData.role === 'user') {
        try {
          const res = await axios.get(`${API}/startups`, { withCredentials: true });
          setHasStartup(Array.isArray(res.data) && res.data.length > 0);
        } catch {
          setHasStartup(false);
        }
      }
    } catch {
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('session_token');
    setUser(null);
    setHasStartup(false);
    window.location.replace('/');
  }, []);

  const handleAdminLogin = useCallback((adminUser) => {
    setUser(adminUser);
  }, []);

  const onOnboardingComplete = useCallback(() => {
    setHasStartup(true);
  }, []);

  // Auth error screen with retry
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
        <div className="text-center max-w-md" data-testid="auth-error-screen">
          <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-100 mb-2">Sign-in interrupted</h2>
          <p className="text-slate-400 text-sm mb-6">{authError}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                if (pendingSessionId) {
                  setAuthError(null);
                  setLoading(true);
                  exchangeSession(pendingSessionId);
                }
              }}
              data-testid="retry-auth-btn"
              className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg text-sm font-medium"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.replace('/')}
              className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm border border-slate-700"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm">Signing you in...</p>
        </div>
      </div>
    );
  }

  const getUserHome = () => {
    if (!user) return '/';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'coach') return '/coach';
    return hasStartup ? '/dashboard' : '/onboarding';
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to={getUserHome()} /> : <LandingPage />} />
          <Route path="/dashboard" element={
            user && user.role === 'user'
              ? (hasStartup ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/onboarding" />)
              : <Navigate to="/" />
          } />
          <Route path="/onboarding" element={
            user && user.role === 'user'
              ? <StartupOnboarding user={user} onComplete={onOnboardingComplete} />
              : <Navigate to="/" />
          } />
          <Route path="/validate" element={
            user && user.role === 'user'
              ? <IdeaValidation user={user} />
              : <Navigate to="/" />
          } />
          <Route path="/knowledge" element={
            user && user.role === 'user'
              ? <KnowledgeBase user={user} onLogout={handleLogout} />
              : <Navigate to="/" />
          } />
          <Route path="/admin/login" element={
            !user
              ? <AdminLogin onLoginSuccess={handleAdminLogin} />
              : <Navigate to={getUserHome()} />
          } />
          <Route path="/admin" element={
            user && user.role === 'admin'
              ? <AdminDashboard user={user} onLogout={handleLogout} />
              : <Navigate to="/admin/login" />
          } />
          <Route path="/admin/onboarding" element={
            user && user.role === 'admin'
              ? <AdminOnboardingReview user={user} onLogout={handleLogout} />
              : <Navigate to="/admin/login" />
          } />
          <Route path="/admin/benchmarks" element={
            user && user.role === 'admin'
              ? <AdminBenchmarks user={user} onLogout={handleLogout} />
              : <Navigate to="/admin/login" />
          } />
          <Route path="/coach" element={
            user && user.role === 'coach'
              ? <CoachDashboard user={user} onLogout={handleLogout} />
              : <Navigate to="/admin/login" />
          } />
          {/* Catch-all: any unmatched path with session_id in hash */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
